'use client'

import { Logomark } from '@/components/brand'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'

/**
 * Minimal shell for a scoped persona (plan 05 CHG-45, CHG-49): a header bar
 * only (brand mark, product line, sign out). No sidebar, no Ask panel, no app
 * navigation. Plan 09 C09-19 uses it for the panelist-only session; it never
 * shows the signed-in person's name. Domain-free on purpose so the AP buyer
 * approval view can reuse it (docs/scoped-approver-view.md).
 */
export function ApproverShell({
  product,
  children,
}: {
  /** For example "Meridian Freight Lines". */
  product: string
  children: React.ReactNode
}) {
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''
  return (
    <div className="min-h-screen">
      <a
        href="#approver-main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-toast focus:rounded-md focus:bg-white focus:px-3 focus:py-2 focus:text-sm focus:text-brand-navy"
      >
        Skip to content
      </a>
      <header className="sticky top-0 z-sticky bg-primary text-white shadow-soft">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3 px-4 py-3 lg:px-8">
          <Logomark variant="light" size={24} />
          <span className="font-display text-base font-bold tracking-tight">Supervity</span>
          <span aria-hidden="true" className="text-white/40">
            ·
          </span>
          <span className="text-sm text-white/85">{product}</span>
          <div className="ml-auto flex items-center gap-3">
            <Button
              asChild
              variant="outline"
              size="sm"
              className="min-h-11 border-white/30 bg-transparent text-white hover:border-white/50 hover:bg-white/10 focus-visible:ring-offset-primary"
            >
              <a href={`${basePath}/api/auth/logout`}>
                <Icons.logout className="h-4 w-4" aria-hidden="true" />
                Sign out
              </a>
            </Button>
          </div>
        </div>
      </header>
      <main id="approver-main" tabIndex={-1} className="mx-auto max-w-6xl px-4 pb-12 pt-6 lg:px-8">
        {children}
      </main>
    </div>
  )
}
